import React, { useEffect, useState, useCallback, useRef } from 'react';
import AWS from 'aws-sdk';
import api from '../api-instance';
import { useNavigate, useParams } from 'react-router-dom';
import NavBar from './NavBar';
import toast, { Toaster } from 'react-hot-toast';
import { fetchBannerTypes, getActiveBannerTypes } from '../bannerTypes';

/* 
Sidebanner1 - 1 image
Sidebanner2 - 2 images
ScrollerFixedBanner - 1 image
CubeBanner - 4 images
HeaderBanner : 2 images
*/

// Helper function to transform API config to component format
const transformConfigToImages = (config, bannerType) => {
  if (!config || !config.images) {
    return [];
  }
  
  return config.images.map((img, index) => ({
    id: img.id || index + 1,
    type: img.type || 'file',
    required: img.required || false,
    name: img.name,
    size: img.size || { width: '100px', height: '200px' },
    ext: img.ext || ['.png', '.jpg', '.gif'],
    selected_file: null,
    uploaded_url: '',
    input_url: '',
    image_url: '',
    previewUrl: '',
    redirectUrl: '',
  }));
};

const AddBanner = () => {

  // Configure AWS SDK
  // const S3_BUCKET = 'ads-banner';
  const REGION = 'ap-south-1';
  const ACCESS_KEY = 'asdasdas';
  const SECRET_KEY = 'adasdas/asdasd+asdasd';

  AWS.config.update({
    accessKeyId: ACCESS_KEY,
    secretAccessKey: SECRET_KEY,
    region: REGION,
  });

  /* const s3 = new AWS.S3({
    params: { Bucket: S3_BUCKET },
    region: REGION,
  }); */

  const navigate = useNavigate();
  const { id } = useParams();
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const todayStr = today.toISOString().split('T')[0];
  const tomorrowStr = tomorrow.toISOString().split('T')[0];
  const [bannerTypes, setBannerTypes] = useState(getActiveBannerTypes());
  const [bannerConfigs, setBannerConfigs] = useState(new Map()); // Store configs by type
  const [bannerConfigsLoaded, setBannerConfigsLoaded] = useState(false);
  const [formData, setFormData] = useState({
    banner_position: '',
    str_story_id: '',
    str_url: '',
    start_date: todayStr,
    end_date: tomorrowStr,
    is_active: 1,
    geo_location: '',
    creatives: '',
    targeting_mode: 'story',
  });

  const [bannerData, setBannerData] = useState({
    id: null,
    name: "",
    type: "",
    images: []
  });

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({}); // Track actual image dimensions: { index: { width, height } }
  const fileInputRefs = useRef({}); // Refs to file inputs for clearing them

  const [availableSections, setAvailableSections] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [sectionSearch, setSectionSearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [showSectionDropdown, setShowSectionDropdown] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);

  useEffect(() => {
    const loadBannerTypes = async () => {
      try {
        const types = await fetchBannerTypes();
        const activeTypes = Array.isArray(types)
          ? types.filter(t => t.active && !t.legacy)
          : getActiveBannerTypes();
        setBannerTypes(activeTypes);
        
        // Build a map of configs by type for quick lookup
        const configMap = new Map();
        activeTypes.forEach(type => {
          if (type.config) {
            configMap.set(type.type, type.config);
          }
        });
        setBannerConfigs(configMap);
        setBannerConfigsLoaded(true);
      } catch (error) {
        console.error('Failed to load banner types', error);
        setBannerTypes(getActiveBannerTypes());
        setBannerConfigsLoaded(true);
      }
    };

    loadBannerTypes();
  }, []);

  useEffect(() => {
    const loadSectionsAndTags = async () => {
      try {
        const [sectionsRes, tagsRes] = await Promise.all([
          api.get('/sections'),
          api.get('/sections/tags'),
        ]);
        if (sectionsRes.data?.data) setAvailableSections(sectionsRes.data.data);
        if (tagsRes.data?.data) setAvailableTags(tagsRes.data.data);
      } catch (error) {
        console.error('Failed to load sections/tags', error);
      }
    };
    loadSectionsAndTags();
  }, []);

  // Helper function to get image dimensions
  const getImageDimensions = useCallback((src, index) => {
    const img = new Image();
    img.onload = () => {
      setImageDimensions(prev => ({
        ...prev,
        [index]: { width: img.naturalWidth, height: img.naturalHeight }
      }));
    };
    img.onerror = () => {
      setImageDimensions(prev => ({
        ...prev,
        [index]: null
      }));
    };
    img.src = src;
  }, []);

  const handleImageChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const updatedImages = [...bannerData.images];
      updatedImages[index].selected_file = file;

      const reader = new FileReader();
      reader.onloadend = () => {
        updatedImages[index].previewUrl = reader.result;
        setBannerData(prevData => ({ ...prevData, images: updatedImages }));
        // Get image dimensions
        getImageDimensions(reader.result, index);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputUrlChange = (e, index) => {
    const { value } = e.target;
    const updatedImages = [...bannerData.images];
    updatedImages[index].input_url = value;
    setBannerData(prevData => ({ ...prevData, images: updatedImages }));
  };

  const handleInputImageUrlChange = (e, index) => {
    const { value } = e.target;
    const updatedImages = [...bannerData.images];
    const trimmed = value.trim();

    updatedImages[index].image_url = value;

    // If the input is cleared, also clear any existing preview/upload data
    if (!trimmed) {
      updatedImages[index].previewUrl = '';
      updatedImages[index].uploaded_url = '';
      updatedImages[index].selected_file = null;
      setImageDimensions(prev => {
        const newDims = { ...prev };
        delete newDims[index];
        return newDims;
      });
    } else {
      // Allow pasting a direct image URL without uploading
      updatedImages[index].previewUrl = value;
      updatedImages[index].uploaded_url = '';
      updatedImages[index].selected_file = null;
      // Get image dimensions from URL
      getImageDimensions(value, index);
    }

    setBannerData(prevData => ({ ...prevData, images: updatedImages }));
  };

  const handleRemoveImage = (index) => {
    const updatedImages = [...bannerData.images];
    updatedImages[index] = {
      ...updatedImages[index],
      selected_file: null,
      uploaded_url: '',
      input_url: '',
      image_url: '',
      previewUrl: '',
    };
    setBannerData(prevData => ({ ...prevData, images: updatedImages }));
    // Clear dimensions
    setImageDimensions(prev => {
      const newDims = { ...prev };
      delete newDims[index];
      return newDims;
    });
    // Clear the file input
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].value = '';
    }
  };

  const fetchBannerData = useCallback(async () => {
    try {
      const response = await api.get(`/banners/${id}`);
      const banner = response.data;
      
      // Map new structure to old form structure for compatibility
      setFormData({
        banner_position: banner.type || banner.slot || banner.name,
        str_story_id: Array.isArray(banner.target?.contentIds) ? banner.target.contentIds.join(',') : '',
        str_url: Array.isArray(banner.target?.storyUrls) && banner.target.storyUrls.length > 0 ? banner.target.storyUrls[0] : '',
        start_date: banner.startAt ? new Date(banner.startAt).toISOString().split('T')[0] : todayStr,
        end_date: banner.endAt ? new Date(banner.endAt).toISOString().split('T')[0] : tomorrowStr,
        is_active: banner.status === 'live' ? 1 : 0,
        geo_location: Array.isArray(banner.target?.tags) && banner.target?.targetingMode === 'story' ? banner.target.tags.join(',') : '',
        creatives: '',
        targeting_mode: banner.target?.targetingMode || 'story',
      });

      if (banner.target?.targetingMode === 'section' || banner.target?.targetingMode === 'section_tag') {
        setSelectedSections(Array.isArray(banner.target.sections) ? banner.target.sections : []);
      }
      if (banner.target?.targetingMode === 'section_tag') {
        setSelectedTags(Array.isArray(banner.target.tags) ? banner.target.tags : []);
      }

      // Map assets to images structure
      const apiConfig = banner.type ? bannerConfigs.get(banner.type) : null;
      const selectedBannerType = banner.type ? bannerTypes.find(bt => bt.type === banner.type) : null;
      const configImages = apiConfig && selectedBannerType
        ? transformConfigToImages(apiConfig, selectedBannerType)
        : [];

      // Map assets to image slots, keeping config names/ordering when possible
      let images = (banner.assets || []).map((asset, index) => {
        const configSlot = configImages[index];
        return {
          id: (configSlot && configSlot.id) || index + 1,
          type: 'file',
          required: (configSlot && configSlot.required) || false,
          name: (configSlot && configSlot.name) || `image_${index + 1}`,
          size: (configSlot && configSlot.size) || (typeof asset.size === 'number' ? asset.size : undefined),
          mime: asset.mime,
          ext: (configSlot && configSlot.ext) || ['.png', '.jpg', '.gif'],
          selected_file: null,
          uploaded_url: asset.url || asset.previewUrl || '',
          input_url: asset.redirectUrl || '',
          image_url: asset.url || asset.previewUrl || '',
          previewUrl: asset.url || asset.previewUrl || '',
        };
      });

      // Pad with empty slots based on config length so removed slots reappear
      if (configImages.length > images.length) {
        for (let i = images.length; i < configImages.length; i++) {
          images.push({
            ...configImages[i],
            id: i + 1,
            name: configImages[i].name || `image_${i + 1}`,
          });
        }
      }

      // If no assets remain (e.g., after removing an image), rebuild image slots from config
      if (!images.length) {
        if (configImages.length) {
          images = configImages;
        }
      }

      setBannerData({
        id: banner.id,
        name: banner.name,
        type: banner.type,
        images: images,
      });

      // Load dimensions for existing images
      const dims = {};
      images.forEach((img, idx) => {
        if (img.previewUrl || img.image_url || img.uploaded_url) {
          const imgSrc = img.previewUrl || img.image_url || img.uploaded_url;
          if (imgSrc) {
            getImageDimensions(imgSrc, idx);
          }
        }
      });
    } catch (err) {
      console.error('Failed to fetch banner data', err);
      toast.error('Failed to load banner data');
      navigate('/banners');
    }
  }, [id, navigate, todayStr, tomorrowStr, bannerConfigs, bannerTypes, getImageDimensions]);

  useEffect(() => {
    if (id && bannerConfigsLoaded) {
      setIsEditing(true);
      fetchBannerData();
    }
  }, [id, bannerConfigsLoaded, fetchBannerData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: name === 'is_active' ? parseInt(value) : value });
  };

  /* const getFormatDateTime = () => {
    let originalDateTime = new Date();

    const year = originalDateTime.getFullYear();
    const month = originalDateTime.getMonth() + 1; // January is 0
    const day = originalDateTime.getDate();
    const hour = originalDateTime.getHours();
    const minute = originalDateTime.getMinutes();

    const formattedDateTime = `${year}${month
      .toString()
      .padStart(2, "0")}${day.toString().padStart(2, "0")}${hour
        .toString()
        .padStart(2, "0")}${minute.toString().padStart(2, "0")}`;

    console.log(formattedDateTime); // Output: "202405021250"
    return formattedDateTime;
  } */

  /* const uploadImageToS3 = async (file) => {
    const params = {
      Body: file,
      Bucket: 'ads-banner',
      Key: `uploads/${file.name}`,
      ContentType: file.type,
    };

    try {
      const data = await s3.upload(params).promise();
      return data.Location;
    } catch (error) {
      console.error('Image upload failed', error);
      throw new Error('Image upload failed');
    }
  }; */

  const getPresignedUrl = async (file) => {
    try {
      const response = await api.post('/uploads/presign', {
        filename: file.name,
        contentType: file.type,
        size: file.size,
      });
      return {
        uploadUrl: response.data.uploadUrl,
        publicUrl: response.data.publicUrl,
        key: response.data.key,
      };
    } catch (err) {
      console.error('Failed to get pre-signed URL', err);
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const tempBannerData = { ...bannerData };
      const assets = [];

      // Upload new files and collect asset data
      for (let i = 0; i < tempBannerData.images.length; i++) {
        const image = tempBannerData.images[i];
        
        if (image.selected_file) {
          // Get presigned URL
          const { uploadUrl, publicUrl, key } = await getPresignedUrl(image.selected_file);

          // Upload file directly to S3
          await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': image.selected_file.type,
            },
            body: image.selected_file,
          });

          assets.push({
            url: publicUrl,
            previewUrl: publicUrl,
            storageKey: key,
            size: image.selected_file.size,
            mime: image.selected_file.type,
            redirectUrl: image.input_url || '',
          });
        } else if (image.uploaded_url || image.image_url || image.previewUrl) {
          // Use existing URL
          const url = image.uploaded_url || image.image_url || image.previewUrl;
          const assetData = {
            url: url,
            previewUrl: url,
            mime: image.mime || 'image/png',
            redirectUrl: image.input_url || '',
          };
          // Only include size if it's a number (not an object)
          if (typeof image.size === 'number') {
            assetData.size = image.size;
          }
          assets.push(assetData);
        }
      }

      // Parse form data to new API structure
      const contentIds = formData.str_story_id 
        ? formData.str_story_id.split(',').map(id => id.trim()).filter(id => id)
        : [];
      const storyUrls = formData.str_url 
        ? [formData.str_url.trim()].filter(url => url)
        : [];
      
      let tags;
      if (formData.targeting_mode === 'section_tag') {
        tags = selectedTags;
      } else {
        tags = formData.geo_location 
          ? formData.geo_location.split(',').map(tag => tag.trim()).filter(tag => tag)
          : [];
      }

      const bannerPayload = {
        name: bannerData.name || formData.banner_position,
        type: bannerData.type,
        slot: bannerData.type,
        target: {
          targetingMode: formData.targeting_mode,
          contentIds,
          storyUrls,
          sections: (formData.targeting_mode === 'section' || formData.targeting_mode === 'section_tag') ? selectedSections : [],
          tags,
          attributes: {},
          categories: [],
        },
        assets,
        metadata: {
          displayName: bannerData.name,
          altText: bannerData.name,
          clickUrl: formData.str_url || '',
          weight: 10,
        },
        status: formData.is_active === 1 ? 'live' : 'draft',
        startAt: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        endAt: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      };

      if (isEditing) {
        await api.put(`/banners/${id}`, bannerPayload);
        toast.success('Banner updated successfully!');
      } else {
        await api.post('/banners', bannerPayload);
        toast.success('Banner added successfully!');
      }

      setTimeout(() => {
        navigate('/banners');
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save banner');
    } finally {
      setLoading(false);
    }
  };

  const handleBannerPositionChange = (e) => {
    const selectedType = e.target.value;
    setFormData(prev => ({ ...prev, banner_position: selectedType }));

    const apiConfig = bannerConfigs.get(selectedType);
    const selectedBannerType = bannerTypes.find(bt => bt.type === selectedType);
    
    if (apiConfig && selectedBannerType) {
      const images = transformConfigToImages(apiConfig, selectedBannerType);
      setBannerData({
        id: selectedBannerType.id,
        name: selectedBannerType.name || selectedBannerType.displayName,
        type: selectedBannerType.type,
        images: images
      });
    }
  };

  // Only show image slots if banner position is selected and images are configured
  const imageSlots = bannerData.images && bannerData.images.length
    ? bannerData.images
    : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <NavBar />
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-6 py-5 border-b border-slate-200">
            <h2 className="text-2xl font-semibold text-slate-900">{isEditing ? 'Edit Banner' : 'Add Banner'}</h2>
            <p className="mt-2 text-sm text-slate-600">To enhance your page's visual appeal, you can add a banner...</p>
          </div>
          
          <form onSubmit={handleSubmit} className="px-6 py-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="banner_position" className="block text-sm font-medium text-slate-700 mb-2">Banner Position</label>
                <select
                  name="banner_position"
                  value={formData.banner_position}
                  onChange={handleBannerPositionChange}
                  className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                  required
                >
                  <option value="">Select a position</option>
                  {bannerTypes.map(pos => (
                    <option key={pos.id} value={pos.type}>
                      {pos.displayName || pos.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="str_url" className="block text-sm font-medium text-slate-700 mb-2">Story URL</label>
                <input 
                  type="url" 
                  name="str_url" 
                  placeholder="Enter URL" 
                  value={formData.str_url} 
                  onChange={handleChange} 
                  className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200" 
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Targeting Mode</label>
                <div className="flex flex-wrap gap-4">
                  {[
                    { value: 'story_url', label: 'Story URL' },
                    { value: 'story', label: 'Story ID' },
                    { value: 'section_tag', label: 'Section + Tags' },
                    { value: 'section', label: 'Section Based' },
                  ].map(mode => (
                    <label key={mode.value} className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors duration-200 ${
                      formData.targeting_mode === mode.value
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}>
                      <input
                        type="radio"
                        name="targeting_mode"
                        value={mode.value}
                        checked={formData.targeting_mode === mode.value}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, targeting_mode: e.target.value }));
                          if (e.target.value === 'story' || e.target.value === 'story_url') {
                            setSelectedSections([]);
                            setSelectedTags([]);
                          }
                        }}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">{mode.label}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">Priority: Story URL &gt; Story ID &gt; Section + Tags &gt; Section</p>
              </div>

              {formData.targeting_mode === 'story' && (
                <div>
                  <label htmlFor="str_story_id" className="block text-sm font-medium text-slate-700 mb-2">Story ID</label>
                  <input 
                    type="text" 
                    name="str_story_id" 
                    placeholder="Eg. 12345677890,1,234324,3" 
                    value={formData.str_story_id} 
                    onChange={handleChange} 
                    className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200" 
                  />
                </div>
              )}

              {(formData.targeting_mode === 'section' || formData.targeting_mode === 'section_tag') && (
                <div className="sm:col-span-2 relative">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Sections</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedSections.map(slug => {
                      const sec = availableSections.find(s => s.slug === slug);
                      return (
                        <span key={slug} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium">
                          {sec ? sec.name : slug}
                          <button
                            type="button"
                            onClick={() => setSelectedSections(prev => prev.filter(s => s !== slug))}
                            className="text-indigo-400 hover:text-indigo-600"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  <input
                    type="text"
                    placeholder="Search sections..."
                    value={sectionSearch}
                    onChange={(e) => setSectionSearch(e.target.value)}
                    onFocus={() => setShowSectionDropdown(true)}
                    onBlur={() => setTimeout(() => setShowSectionDropdown(false), 200)}
                    className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                  />
                  {showSectionDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {availableSections
                        .filter(s => !selectedSections.includes(s.slug))
                        .filter(s => s.name.toLowerCase().includes(sectionSearch.toLowerCase()) || s.slug.toLowerCase().includes(sectionSearch.toLowerCase()))
                        .map(s => (
                          <button
                            key={s.slug}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setSelectedSections(prev => [...prev, s.slug]);
                              setSectionSearch('');
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-indigo-50 hover:text-indigo-700 transition-colors duration-150"
                          >
                            {s.name} <span className="text-slate-400">({s.slug})</span>
                          </button>
                        ))}
                      {availableSections
                        .filter(s => !selectedSections.includes(s.slug))
                        .filter(s => s.name.toLowerCase().includes(sectionSearch.toLowerCase()) || s.slug.toLowerCase().includes(sectionSearch.toLowerCase()))
                        .length === 0 && (
                        <div className="px-4 py-2 text-sm text-slate-400">No sections found</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {formData.targeting_mode === 'section_tag' && (
                <div className="sm:col-span-2 relative">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tags</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedTags.map(slug => {
                      const tag = availableTags.find(t => t.slug === slug);
                      return (
                        <span key={slug} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium">
                          {tag ? tag.name : slug}
                          <button
                            type="button"
                            onClick={() => setSelectedTags(prev => prev.filter(t => t !== slug))}
                            className="text-emerald-400 hover:text-emerald-600"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  <input
                    type="text"
                    placeholder="Search tags..."
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    onFocus={() => setShowTagDropdown(true)}
                    onBlur={() => setTimeout(() => setShowTagDropdown(false), 200)}
                    className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                  />
                  {showTagDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {availableTags
                        .filter(t => !selectedTags.includes(t.slug))
                        .filter(t => t.name.toLowerCase().includes(tagSearch.toLowerCase()) || t.slug.toLowerCase().includes(tagSearch.toLowerCase()))
                        .map(t => (
                          <button
                            key={t.slug}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setSelectedTags(prev => [...prev, t.slug]);
                              setTagSearch('');
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-emerald-50 hover:text-emerald-700 transition-colors duration-150"
                          >
                            {t.name} <span className="text-slate-400">({t.slug})</span>
                          </button>
                        ))}
                      {availableTags
                        .filter(t => !selectedTags.includes(t.slug))
                        .filter(t => t.name.toLowerCase().includes(tagSearch.toLowerCase()) || t.slug.toLowerCase().includes(tagSearch.toLowerCase()))
                        .length === 0 && (
                        <div className="px-4 py-2 text-sm text-slate-400">No tags found</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div>
                <label htmlFor="start_date" className="block text-sm font-medium text-slate-700 mb-2">Start Date</label>
                <input 
                  type="date" 
                  name="start_date" 
                  value={formData.start_date} 
                  onChange={handleChange} 
                  className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200" 
                />
              </div>

              <div>
                <label htmlFor="end_date" className="block text-sm font-medium text-slate-700 mb-2">End Date</label>
                <input 
                  type="date" 
                  name="end_date" 
                  value={formData.end_date} 
                  onChange={handleChange} 
                  className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200" 
                />
              </div>

              <div>
                <label htmlFor="is_active" className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                <select 
                  name="is_active" 
                  value={formData.is_active} 
                  onChange={handleChange} 
                  className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div>
                <label htmlFor="geo_location" className="block text-sm font-medium text-slate-700 mb-2">Tags (comma separated)</label>
                <input 
                  type="text" 
                  name="geo_location" 
                  placeholder="Enter tags" 
                  value={formData.geo_location} 
                  onChange={handleChange} 
                  className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200" 
                />
              </div>
            </div>

            {imageSlots.length > 0 && (
              <div className="mt-8 space-y-4">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Banner Images</h3>
                {imageSlots.map((imageData, index) => {
                  const isSideBanner2OrTower2 = ['sidebanner2', 'tower2'].includes(formData.banner_position);
                  let tallSlotIndex = -1;
                  if (isSideBanner2OrTower2) {
                    for (let i = 0; i < imageSlots.length; i++) {
                      if (imageDimensions[i]?.height >= 550) {
                        tallSlotIndex = i;
                        break;
                      }
                    }
                  }
                  const isDisabled = isSideBanner2OrTower2 && tallSlotIndex !== -1 && tallSlotIndex !== index;

                  return (
                    <div 
                      className={`border border-slate-200 rounded-lg p-5 transition-opacity duration-200 ${isDisabled ? 'bg-slate-100 opacity-60' : 'bg-slate-50'}`} 
                      key={index}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            {imageData.name} 
                            {isDisabled && <span className="ml-2 text-xs text-amber-600 font-normal">(Deactivated by main banner)</span>}
                          </label>
                          
                          <div className="mb-3 space-y-1">
                            {imageData.size && typeof imageData.size === 'object' && imageData.size.width && imageData.size.height && (
                              <div className="text-xs text-slate-600">
                                <span className="font-medium">Required dimensions:</span> {imageData.size.width} × {imageData.size.height}
                              </div>
                            )}
                            {imageDimensions[index] && (
                              <div className={`text-xs font-medium ${
                                imageData.size && typeof imageData.size === 'object' && imageData.size.width && imageData.size.height
                                  ? 'text-green-600'
                                  : 'text-slate-700'
                              }`}>
                                <span className="font-medium">Actual dimensions:</span> {imageDimensions[index].width}px × {imageDimensions[index].height}px
                              </div>
                            )}
                          </div>

                          { !imageData.image_url && (
                            <input 
                              ref={(el) => { fileInputRefs.current[index] = el; }}
                              type="file" 
                              accept="image/*"
                              disabled={isDisabled}
                              onChange={(e) => handleImageChange(e, index)} 
                              className="mt-1 block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-70" 
                            />
                          )}
                          <input 
                            type="url" 
                            name={`input_url_${index}`} 
                            placeholder={`Redirect URL for ${imageData.name} (click destination)`} 
                            value={imageData.input_url} 
                            disabled={isDisabled}
                            onChange={(e) => handleInputUrlChange(e, index)} 
                            className="mt-3 w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200 disabled:bg-slate-100 disabled:cursor-not-allowed" 
                          />
                          <input 
                            type="url" 
                            name={`image_url_${index}`} 
                            placeholder={`Image URL for ${imageData.name}`} 
                            value={imageData.image_url} 
                            disabled={isDisabled}
                            onChange={(e) => handleInputImageUrlChange(e, index)} 
                            className="mt-3 w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors duration-200 disabled:bg-slate-100 disabled:cursor-not-allowed" 
                          />
                          {(imageData.previewUrl || imageData.image_url) && (
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              disabled={isDisabled}
                              className="mt-3 text-sm text-red-600 hover:text-red-700 font-medium transition-colors duration-200 disabled:text-red-300 disabled:cursor-not-allowed"
                            >
                              Remove image
                            </button>
                          )}
                        </div>
                        <div className="flex items-center justify-center">
                          {(imageData.previewUrl || imageData.image_url) && (
                            <img 
                              src={imageData.previewUrl || imageData.image_url || null} 
                              alt={`Preview of ${imageData.name}`} 
                              className="border border-slate-200 max-h-32 object-contain" 
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          
            <div className="mt-8 flex justify-end border-t border-slate-200 pt-6">
              <button 
                type="submit" 
                className="px-6 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" 
                disabled={loading}
              >
                {loading ? 'Saving...' : isEditing ? 'Update Banner' : 'Add Banner'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default AddBanner;
